import React, { useState, useEffect } from 'react';
import './Profile.css'; // Add CSS for animations
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import { API_ENDPOINTS } from '../../config/api';

// Theme context - in real app this would come from context
const getCurrentTheme = () => 'dark'; // Mock theme getter
const setCurrentTheme = (theme: string) => {
  // In real app, this would update the theme context
};

const colors = getThemeColors('dark');

const Profile: React.FC = () => {
  const [editing, setEditing] = useState(false);
  const [theme, setTheme] = useState(getCurrentTheme());
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    county: '',
    town: '',
    bio: '',
    occupation: '',
    dateOfBirth: '',
    gender: '',
  });
  const [originalData, setOriginalData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    county: '',
    town: '',
    bio: '',
    occupation: '',
    dateOfBirth: '',
    gender: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [avatarData, setAvatarData] = useState<string | null>(null);
  const [imageExpanded, setImageExpanded] = useState(false);


  useEffect(() => {
    // Check if user is authenticated before loading profile data
    const authToken = localStorage.getItem('authToken');
    const storedUserData = localStorage.getItem('userData');

    if (!authToken) {
      window.location.href = '/login';
      return;
    }

    // Load profile data on component mount
    loadProfileData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfileData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No auth token found - user may not be logged in');
        alert('Please log in to view your profile');
        window.location.href = '/login';
        return;
      }

     
      const response = await fetch(API_ENDPOINTS.USER_PROFILE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      
      const data = await response.json();
      if (data.success) {
        // Access the user data from the correct nested structure: data.data.user
        const apiUser = data.data.user || data.data;

        // Helper function to get field value from API with camelCase/snake_case support
        const getApiFieldValue = (fieldName: string) => {
          // Try camelCase first, then snake_case
          return apiUser[fieldName] || apiUser[fieldName.replace(/([A-Z])/g, '_$1').toLowerCase()] || '';
        };

        // Build profile data directly from API response
        const profileData = {
          firstName: getApiFieldValue('firstName'),
          lastName: getApiFieldValue('lastName'),
          email: getApiFieldValue('email'),
          phone: getApiFieldValue('phone'),
          county: getApiFieldValue('county'),
          town: getApiFieldValue('town'),
          bio: getApiFieldValue('bio'),
          occupation: getApiFieldValue('occupation'),
          dateOfBirth: getApiFieldValue('dateOfBirth'),
          gender: getApiFieldValue('gender'),
        };

        Object.entries(profileData).forEach(([key, value]) => {
          const apiValue = getApiFieldValue(key === 'firstName' ? 'firstName' : key === 'lastName' ? 'lastName' : key === 'dateOfBirth' ? 'dateOfBirth' : key);
        });

        setUserData(profileData);
        setOriginalData(profileData); // Store original data for comparison
        setIsLoading(false);

        // Handle avatar data from API
        if (apiUser.avatar) {
          let avatarUrl = apiUser.avatar;
          if (!avatarUrl.startsWith('data:image/')) {
            avatarUrl = `data:image/jpeg;base64,${avatarUrl}`;
            } else {
          }
          setAvatarData(avatarUrl);
        } else {
          setAvatarData(null);
        }

      } else {
        console.error('Failed to load profile:', data.error);
        setIsLoading(false);
        // Show error message instead of fallback data
        alert('Failed to load profile data. Please try again.');
        // If authentication error, redirect to login
        if (data.error && (data.error.includes('auth') || data.error.includes('token') || data.error.includes('unauthorized'))) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setIsLoading(false);
      // Show error message instead of fallback data
      alert('Network error loading profile. Please check your connection and try again.');
      // If it's a 401/403 error, redirect to login
      if (error instanceof Response && (error.status === 401 || error.status === 403)) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const newData = {
      ...userData,
      [field]: value,
    };
    setUserData(newData);
  };

  const hasUnsavedChanges = () => {
    const hasChanges = JSON.stringify(userData) !== JSON.stringify(originalData) || avatarData !== null;
    // Debug logging - show change detection
    if (hasChanges) {
    }
    return hasChanges;
  };

  const handleSave = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('Authentication required. Please login again.');
        window.location.href = '/login';
        return;
      }

      // Prepare data for submission, ensuring avatar is properly formatted
      const submitData: any = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        county: userData.county,
        town: userData.town,
        bio: userData.bio,
        occupation: userData.occupation,
        dateOfBirth: userData.dateOfBirth,
        gender: userData.gender,
      };
      if (avatarData && avatarData.startsWith('data:image/')) {
        // If we have a data URL, extract just the base64 part for backend storage
        const base64Data = avatarData.split(',')[1];
        submitData.avatar = base64Data;
      }

      const response = await fetch(API_ENDPOINTS.USER_PROFILE, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        setEditing(false);
        alert('Profile updated successfully!');
        // Debug logging - show successful save response
        // Update the original data with the new data and reload to get fresh data from server
        loadProfileData();
      } else {
        alert(data.error || 'Failed to update profile. Please try again.');
        // If authentication error, redirect to login
        if (data.error && (data.error.includes('auth') || data.error.includes('token') || data.error.includes('unauthorized'))) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert('Network error. Please try again.');
      // If it's a 401/403 error, redirect to login
      if (error instanceof Response && (error.status === 401 || error.status === 403)) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
      }
    }
  };

  const handleCancel = () => {
    // Reset to original data stored in memory
    setUserData(originalData);
    setEditing(false);
  };

  const handleImagePress = () => {
    if (editing) {
      // Open file picker for image upload
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64Data = event.target?.result as string;
            setAvatarData(base64Data);
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    } else {
      setImageExpanded(!imageExpanded);
    }
  };

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setCurrentTheme(newTheme);
  };

  const handleLogout = () => {
    alert('Logout functionality would be implemented here');
  };

  const navigateToSettings = () => {
    // Navigate to settings page - in real app this would use router
    window.location.href = '/dashboard/settings';
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    profileCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: imageExpanded ? 0 : spacing.xl,
      marginBottom: spacing.xl,
      border: `2px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      transition: 'all 0.3s ease',
      overflow: 'hidden',
    },
    framelessCard: {
      padding: 0,
      overflow: 'hidden',
    },
    framelessProfileLayout: {
      position: 'relative',
      overflow: 'hidden',
    },
    minimizeButton: {
      position: 'absolute',
      top: spacing.sm,
      right: spacing.sm,
      zIndex: 10,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      borderRadius: 20,
      padding: spacing.sm,
      cursor: 'pointer',
      color: colors.white,
      fontSize: typography.fontSize.lg,
    },
    framelessImageContainer: {
      width: '100%',
      alignItems: 'center',
    },
    framelessImage: {
      width: '100%',
      height: 400,
      borderTopLeftRadius: borderRadius.xl,
      borderTopRightRadius: borderRadius.xl,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      backgroundColor: colors.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '120px',
      color: colors.white,
    },
    framelessProfileInfo: {
      alignItems: 'center',
      padding: spacing.lg,
      backgroundColor: 'transparent',
    },
    minimizeHint: {
      fontSize: typography.fontSize.sm,
      fontStyle: 'italic',
      marginTop: spacing.sm,
      color: colors.textSecondary,
    },
    framelessHeaderActions: {
      display: 'flex',
      flexDirection: 'row',
      gap: spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: spacing.lg,
      paddingRight: spacing.lg,
      paddingBottom: spacing.lg,
    },
    location: {
      fontSize: typography.fontSize.sm,
      color: colors.textTertiary,
      textAlign: 'center',
    },
    avatarSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: spacing.xl,
    },
    avatar: {
      width: 140,
      height: 140,
      borderRadius: 70,
      backgroundColor: colors.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: typography.fontSize['5xl'],
      color: colors.white,
      marginBottom: spacing.lg,
      border: `6px solid ${colors.primary}`,
      cursor: editing ? 'pointer' : 'zoom-in',
      position: 'relative',
      boxShadow: `0 8px 24px ${colors.primary}40`,
      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    avatarOverlay: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: editing ? colors.primary : colors.info,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `2px solid ${colors.surface}`,
    },
    actionIcons: {
      display: 'flex',
      gap: spacing.sm,
      marginTop: spacing.md,
      marginBottom: spacing.sm,
    },
    actionButton: {
      padding: spacing.md,
      borderRadius: borderRadius.full,
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 56,
      height: 56,
      fontSize: 24,
      boxShadow: `0 4px 12px ${colors.shadow}`,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    themeButton: {
      backgroundColor: colors.primary + '20',
      color: colors.primary,
      border: `1px solid ${colors.primary}`,
    },
    settingsButton: {
      backgroundColor: colors.primary + '20',
      color: colors.primary,
    },
    logoutButton: {
      backgroundColor: colors.error + '20',
      color: colors.error,
    },
    editButton: {
      backgroundColor: colors.primary + '20',
      color: colors.primary,
    },
    name: {
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
      textAlign: 'center',
    },
    email: {
      fontSize: typography.fontSize.lg,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    section: {
      marginBottom: spacing.xl,
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `3px solid ${colors.primary}`,
      textAlign: 'center',
    },
    field: {
      marginBottom: spacing.lg,
    },
    fieldLabel: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.sm,
      textTransform: 'capitalize',
      letterSpacing: 0.3,
    },
    fieldValue: {
      fontSize: typography.fontSize.lg,
      color: colors.text,
      padding: spacing.lg,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      minHeight: 56,
      display: 'flex',
      alignItems: 'center',
      fontWeight: typography.fontWeight.medium,
    },
    fieldInput: {
      fontSize: typography.fontSize.lg,
      color: colors.text,
      padding: spacing.lg,
      backgroundColor: colors.background,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      width: '100%',
      outline: 'none',
      minHeight: 56,
      fontWeight: typography.fontWeight.medium,
      transition: 'border-color 0.2s ease',
    },
    actions: {
      display: 'flex',
      gap: spacing.sm,
      marginTop: spacing.lg,
    },
    button: {
      padding: `${spacing.lg} ${spacing.xl}`,
      borderRadius: borderRadius.lg,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      border: 'none',
      flex: 1,
      minHeight: 56,
      boxShadow: `0 4px 12px ${colors.shadow}`,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    primaryButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: colors.primary,
      border: `1px solid ${colors.primary}`,
    },
    dangerButton: {
      backgroundColor: colors.error,
      color: colors.white,
    },
    imageModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      animation: 'fadeIn 0.3s ease',
      backdropFilter: 'blur(4px)',
    },
    imageModalContent: {
      position: 'relative',
      maxWidth: '90vw',
      maxHeight: '90vh',
      animation: 'zoomIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      transformOrigin: 'center',
    },
    enlargedAvatar: {
      width: 320,
      height: 320,
      borderRadius: 160,
      backgroundColor: colors.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '120px',
      color: colors.white,
      border: `10px solid ${colors.primary}`,
      boxShadow: `0 25px 80px ${colors.primary}70, 0 0 40px ${colors.primary}30`,
      animation: 'pulse 2s infinite',
    },
    closeButton: {
      position: 'absolute',
      top: -25,
      right: -25,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: colors.surface,
      border: `3px solid ${colors.border}`,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 28,
      color: colors.text,
      boxShadow: `0 6px 20px ${colors.shadow}`,
      transition: 'all 0.2s ease',
    },
    closeButtonHover: {
      transform: 'scale(1.1)',
      boxShadow: `0 8px 25px ${colors.shadow}`,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Profile"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {isLoading ? (
            <div style={{ ...styles.profileCard, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
                <div style={{ ...styles.name, marginBottom: '8px' }}>Loading Profile...</div>
                <div style={styles.email}>Please wait while we fetch your data</div>
              </div>
            </div>
          ) : (
          <div style={{ ...styles.profileCard, ...(imageExpanded ? styles.framelessCard : {}) }}>
            {imageExpanded ? (
              // Expanded layout: Frameless image at top, then info below
              <div style={styles.framelessProfileLayout}>
                {/* Minimize button positioned absolutely */}
                <div style={styles.minimizeButton} onClick={handleImagePress}>
                  ✕
                </div>

                {/* Frameless Image Section - touches top, left, and right edges */}
                <div style={styles.framelessImageContainer}>
                  <div style={styles.framelessImage}>
                    {avatarData ? (
                      <img
                        src={avatarData}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          borderTopLeftRadius: borderRadius.xl,
                          borderTopRightRadius: borderRadius.xl,
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          console.error('Expanded image failed to load:', avatarData.substring(0, 100) + '...');
                          console.error('Expanded image error event:', e);
                          console.error('Expanded image src attribute:', e.currentTarget.src);
                          // Fallback to initials if image fails
                          setAvatarData(null);
                        }}
                      />
                    ) : (
                      <span>{userData.firstName.charAt(0)}{userData.lastName.charAt(0)}</span>
                    )}
                  </div>
                </div>

                {/* Profile Info Section - Below the image */}
                <div style={styles.framelessProfileInfo}>
                  <h1 style={styles.name}>
                    {userData.firstName} {userData.lastName}
                  </h1>
                  <p style={styles.email}>{userData.email}</p>
                  <p style={styles.location}>{userData.town}, {userData.county}</p>

                  <p style={styles.minimizeHint}>
                    Tap the × to minimize
                  </p>
                </div>

                {/* Action Buttons - Below info */}
                <div style={styles.framelessHeaderActions}>
                  <button
                    style={{ ...styles.actionButton, ...styles.themeButton }}
                    onClick={handleThemeToggle}
                    title="Toggle Theme"
                  >
                    {theme === 'light' ? '🌙' : '☀️'}
                  </button>

                  <button
                    style={{ ...styles.actionButton, ...styles.logoutButton }}
                    onClick={handleLogout}
                    title="Logout"
                  >
                    🚪
                  </button>

                  <button
                    style={{ ...styles.actionButton, ...styles.settingsButton }}
                    onClick={navigateToSettings}
                    title="Settings"
                  >
                    ⚙️
                  </button>

                  <button
                    style={{ ...styles.actionButton, ...styles.editButton }}
                    onClick={() => {
                      if (editing && hasUnsavedChanges()) {
                        if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                          handleCancel();
                        }
                      } else {
                        setEditing(!editing);
                      }
                    }}
                    title={editing ? "Cancel Edit" : "Edit Profile"}
                  >
                    {editing ? '✖️' : '✏️'}
                  </button>
                </div>
              </div>
            ) : (
              // Normal layout: Avatar section with info
              <>
                <div style={styles.avatarSection}>
              <div style={styles.avatar} onClick={handleImagePress}>
                {avatarData ? (
                  <img
                    src={avatarData}
                    alt="Profile"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      console.error('Image failed to load:', avatarData.substring(0, 100) + '...');
                      console.error('Image error event:', e);
                      console.error('Image src attribute:', e.currentTarget.src);
                      // Fallback to initials if image fails
                      setAvatarData(null);
                    }}
                  />
                ) : (
                  <span>{userData.firstName.charAt(0)}{userData.lastName.charAt(0)}</span>
                )}
                {editing && (
                  <div style={styles.avatarOverlay}>
                    📷
                  </div>
                )}
                {!editing && (
                  <div style={styles.avatarOverlay}>
                    🔍
                  </div>
                )}
              </div>
              <h1 style={styles.name}>
                {userData.firstName} {userData.lastName}
              </h1>
              <p style={styles.email}>{userData.email}</p>

              {/* Action Icons Row */}
              <div style={styles.actionIcons}>
                <button
                  style={{ ...styles.actionButton, ...styles.themeButton }}
                  onClick={handleThemeToggle}
                  title="Toggle Theme"
                >
                  {theme === 'light' ? '🌙' : '☀️'}
                </button>

                <button
                  style={{ ...styles.actionButton, ...styles.logoutButton }}
                  onClick={handleLogout}
                  title="Logout"
                >
                  🚪
                </button>

                <button
                  style={{ ...styles.actionButton, ...styles.settingsButton }}
                  onClick={navigateToSettings}
                  title="Settings"
                >
                  ⚙️
                </button>

                <button
                  style={{ ...styles.actionButton, ...styles.editButton }}
                  onClick={() => {
                    if (editing && hasUnsavedChanges()) {
                      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                        handleCancel();
                      }
                    } else {
                      setEditing(!editing);
                    }
                  }}
                  title={editing ? "Cancel Edit" : "Edit Profile"}
                >
                  {editing ? '✖️' : '✏️'}
                </button>
              </div>
                </div>
              </>
            )}

            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Personal Information</h2>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>First Name</label>
                {editing ? (
                  <input
                    type="text"
                    style={styles.fieldInput}
                    value={userData.firstName}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                  />
                ) : (
                  <div style={styles.fieldValue}>{userData.firstName}</div>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>Last Name</label>
                {editing ? (
                  <input
                    type="text"
                    style={styles.fieldInput}
                    value={userData.lastName}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                  />
                ) : (
                  <div style={styles.fieldValue}>{userData.lastName}</div>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>Email</label>
                {editing ? (
                  <input
                    type="email"
                    style={styles.fieldInput}
                    value={userData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                ) : (
                  <div style={styles.fieldValue}>{userData.email}</div>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>Phone</label>
                {editing ? (
                  <input
                    type="tel"
                    style={styles.fieldInput}
                    value={userData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                ) : (
                  <div style={styles.fieldValue}>{userData.phone}</div>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>Gender</label>
                {editing ? (
                  <select
                    style={styles.fieldInput}
                    value={userData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                ) : (
                  <div style={styles.fieldValue}>
                    {userData.gender ?
                      (['male', 'female', 'other', 'prefer_not_to_say'].find(g => g === userData.gender) ?
                        userData.gender.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                        userData.gender) :
                      'Not specified'
                    }
                  </div>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>Occupation</label>
                {editing ? (
                  <input
                    type="text"
                    style={styles.fieldInput}
                    value={userData.occupation}
                    onChange={(e) => handleInputChange('occupation', e.target.value)}
                    placeholder="Enter your occupation"
                  />
                ) : (
                  <div style={styles.fieldValue}>{userData.occupation || 'Not specified'}</div>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>Date of Birth</label>
                {editing ? (
                  <input
                    type="date"
                    style={styles.fieldInput}
                    value={userData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  />
                ) : (
                  <div style={styles.fieldValue}>
                    {userData.dateOfBirth ?
                      new Date(userData.dateOfBirth).toLocaleDateString() :
                      'Not specified'
                    }
                  </div>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>Location</label>
                {editing ? (
                  <div style={{ display: 'flex', gap: spacing.sm }}>
                    <input
                      type="text"
                      style={{ ...styles.fieldInput, flex: 1 }}
                      placeholder="Town"
                      value={userData.town}
                      onChange={(e) => handleInputChange('town', e.target.value)}
                    />
                    <input
                      type="text"
                      style={{ ...styles.fieldInput, flex: 1 }}
                      placeholder="County"
                      value={userData.county}
                      onChange={(e) => handleInputChange('county', e.target.value)}
                    />
                  </div>
                ) : (
                  <div style={styles.fieldValue}>{userData.town}, {userData.county}</div>
                )}
              </div>
            </div>

            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>About</h2>
              <div style={styles.field}>
                <label style={styles.fieldLabel}>Bio</label>
                {editing ? (
                  <textarea
                    style={{ ...styles.fieldInput, minHeight: 80, resize: 'vertical' }}
                    value={userData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    rows={3}
                  />
                ) : (
                  <div style={styles.fieldValue}>{userData.bio}</div>
                )}
              </div>
            </div>

            {editing && (
              <div style={styles.actions}>
                <button
                  style={{ ...styles.button, ...styles.primaryButton }}
                  onClick={handleSave}
                >
                  Save Changes
                </button>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Profile;
